import { postSaveMemory } from 'api/goodtrust/user/memory'
import { Button } from 'components/button/Button'
import { SNewVideoActions } from 'components/content/singingPortraits/headerActions/styled'
import { Icon } from 'components/icon'
import { BasicLink } from 'components/Link'
import { SelectedMemory } from 'components/memory/MemoryUtils'
import { useAuthModal } from 'components/modal/authModal/AuthModal'
import { Spacer } from 'components/spacer/Spacer'
import { handleAndToastError, toastSuccessString } from 'components/Toast'
import { Trans, useTranslation } from 'next-i18next'
import Router from 'next/router'
import { useState } from 'react'
import { fireEvent } from 'utils/analytics'
import { usePolyfilledDownload } from 'utils/file'
import { useLoadingCallback, useWindowSize } from 'utils/hooks'
import { GenericAsyncFunction } from 'utils/types'

export function SharedSPActions(props: {
  memory?: SelectedMemory
  className?: string
  onDownload?: GenericAsyncFunction
}) {
  const { t } = useTranslation()
  const { t: tMemory } = useTranslation('memory')
  const { isDesktop } = useWindowSize()

  const [memoryAdded, setMemoryAdded] = useState(false)

  const [authModal, authWithModal, closeAuthModal] = useAuthModal({
    config: {
      source: 'LIFE_STORY',
    },
  })

  const downloadFile = usePolyfilledDownload()

  const [handleDownload, isDownloading] = useLoadingCallback(async () => {
    if (props.memory?.selectedAnimation?.file) {
      await downloadFile(props.memory.selectedAnimation.file)
    } else {
      props.memory?.original_file && (await downloadFile?.(props.memory.original_file))
    }

    await props.onDownload?.()
  })

  const [addToMyMemories, isAdding] = useLoadingCallback(async () => {
    authWithModal(async () => {
      closeAuthModal()
      fireEvent('sh_animation_added')
      try {
        if (props.memory?.uuid == null) throw Error()
        const res = await postSaveMemory(props.memory.uuid, props.memory.selectedAnimation?.uuid)
        if (!res.ok) throw Error(res?.error?.toString?.())

        toastSuccessString(
          <Trans
            t={t}
            i18nKey="common.success_codes.singing_portrait_saved"
            components={[
              <BasicLink
                css={{ color: 'var(--c-white)' }}
                key="0"
                onClick={navigateToMyMemories}
              />,
            ]}
          />
        )
        await setMemoryAdded(true)
      } catch (err) {
        handleAndToastError(err, 'failed_to_add_memory')
      }
    })
  })

  const navigateToMyMemories = async () => {
    await Router.push('/me/life-stories')
  }

  return (
    <SNewVideoActions className={props.className}>
      {authModal}

      <Button
        type="button"
        variant="text"
        loading={isDownloading}
        leftIcon={<Icon name="FileDownloadFrame" />}
        onClick={handleDownload}
      >
        {isDesktop
          ? tMemory('memory.memory_id.sp_actions.download_desktop')
          : tMemory('memory.memory_id.sp_actions.download_mobile')}
      </Button>

      <Spacer size={2} axis={'horizontal'} />

      {memoryAdded ? (
        <Button
          type="button"
          variant="text"
          leftIcon={<Icon name="ChevronRight" />}
          onClick={navigateToMyMemories}
        >
          {tMemory('memory.memory_id.sp_actions.view_library_added')}
        </Button>
      ) : (
        <Button
          type="button"
          variant="text"
          leftIcon={<Icon name="Plus" />}
          loading={isAdding}
          onClick={addToMyMemories}
        >
          {tMemory('memory.memory_id.sp_actions.view_library_not_added')}
        </Button>
      )}
    </SNewVideoActions>
  )
}
